import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import axios from 'axios';
import { Notify } from '../../Utility/Notify';
import { db_url } from '../../Api/BaseUrl';
import { useSidebarWidth } from '../../Utility/useSidebarWidth';
import Pf_image from '../../images/ProPerty Finder image.png';
// import Pf_imagescopes from '../../images/pfscopes.png';

/* ─── Icons ──────────────────────────────────────────────────────────────── */
const IconInfo   = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>;
const IconClose  = () => <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
const IconZoom    = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>;

/* ─── Styles ──────────────────────────────────────────────────────────────── */
const inputSt = (isDark) => ({
  width: '100%', padding: '12px 14px', borderRadius: '10px', fontSize: '13px',
  border: `1.5px solid ${isDark ? '#2d3248' : '#e2e8f0'}`, backgroundColor: isDark ? '#151827' : '#f8fafc',
  color: isDark ? '#f1f5f9' : '#0f172a', fontFamily: "'DM Sans',sans-serif", outline: 'none', boxSizing: 'border-box',
});
const labelSt = (isDark) => ({
  display: 'block', fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
  color: isDark ? '#6b7280' : '#64748b', marginBottom: '7px', fontFamily: "'DM Sans',sans-serif",
});

/* ─── Step badge (wizard) ────────────────────────────────────────────────── */
const StepBadge = ({ n, active, done, isDark }) => (
  <div style={{ width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0, background: done ? '#16a34a' : active ? '#1d4ed8' : (isDark ? '#2a2f45' : '#e2e8f0'), color: done || active ? '#fff' : (isDark ? '#6b7280' : '#94a3b8') }}>
    {done ? '✓' : n}
  </div>
);

/* ─── Guide sub-components ───────────────────────────────────────────────── */
const GuideStep = ({ n, title, children, isDark }) => (
  <div style={{ display: 'flex', gap: 12, marginBottom: 18 }}>
    <div style={{ width: 24, height: 24, borderRadius: '50%', background: isDark ? '#2a2f45' : '#e2e8f0', color: isDark ? '#93c5fd' : '#1d4ed8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
      {n}
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: isDark ? '#f1f5f9' : '#0f172a', marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 12.5, color: isDark ? '#9ca3af' : '#475569', lineHeight: 1.6 }}>{children}</div>
    </div>
  </div>
);

/* Clickable image → opens fullscreen lightbox via onZoom callback */
const GuideImage = ({ src, alt, isDark, onZoom }) => (
  <div
    onClick={() => onZoom(src, alt)}
    style={{ position: 'relative', marginTop: 8, marginBottom: 4, borderRadius: 10, overflow: 'hidden', border: `1px solid ${isDark ? '#2a2f45' : '#e2e8f0'}`, background: isDark ? '#151827' : '#f8fafc', cursor: 'zoom-in' }}
  >
    <img src={src} alt={alt} style={{ width: '100%', display: 'block' }} />
    <span style={{ position: 'absolute', bottom: 8, right: 8, background: 'rgba(0,0,0,0.55)', color: '#fff', borderRadius: 8, padding: '5px 8px', display: 'flex', alignItems: 'center', gap: 5, fontSize: 10.5, fontWeight: 600 }}>
      <IconZoom /> View full size
    </span>
  </div>
);

/* Fullscreen lightbox overlay */
const ImageLightbox = ({ src, alt, onClose }) => {
  if (!src) return null;
  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 200000, background: 'rgba(0,0,0,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32, cursor: 'zoom-out' }}
    >
      <span onClick={onClose} style={{ position: 'absolute', top: 20, right: 24, color: '#fff', cursor: 'pointer', display: 'flex' }}>
        <IconClose />
      </span>
      <img
        src={src}
        alt={alt}
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '92vw', maxHeight: '90vh', objectFit: 'contain', borderRadius: 8, boxShadow: '0 20px 60px rgba(0,0,0,0.5)', cursor: 'default' }}
      />
    </div>
  );
};

/* ─── Main Page ───────────────────────────────────────────────────────────── */
export const PFConfigurationPage = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const sidebarWidth = useSidebarWidth();

  const [config, setConfig] = useState(null);
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [loading, setLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [subscribeResult, setSubscribeResult] = useState(null);
  const [lightbox, setLightbox] = useState({ src: null, alt: '' });

  const openLightbox = (src, alt) => setLightbox({ src, alt });
  const closeLightbox = () => setLightbox({ src: null, alt: '' });

  const fetchConfig = async () => {
    try {
      const { data } = await axios.get(`${db_url}api/v1/pf-config`);
      setConfig(data.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchConfig(); }, []);

  const handleSaveCredentials = async () => {
    if (!apiKey || !apiSecret) return Notify('error', ' ُُEnter The API Key and The Secret', 'error');
    setLoading(true);
    try {
      await axios.post(`${db_url}api/v1/pf-config/credentials`, { apiKey, apiSecret });
      Notify('success', 'Saved Done ', 'success');
      setApiKey(''); setApiSecret('');
      fetchConfig();
    } catch (err) {
      Notify('error', err.response?.data?.message || ' Error Occured', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${db_url}api/v1/pf-config/test-connection`);
      setTestResult({ success: true, message: data.message });
      Notify('success', data.message, 'success');
    } catch (err) {
      setTestResult({ success: false, message: err.response?.data?.message });
      Notify('error', err.response?.data?.message || 'Connecting Faild', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${db_url}api/v1/pf-config/subscribe`);
      setSubscribeResult(data.data);
      Notify('success', 'Connecting Done Successfully    ✅', 'success');
      fetchConfig();
    } catch (err) {
      Notify('error', err.response?.data?.message || ' Faild Activation', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUnsubscribe = async () => {
    if (!window.confirm('Do You Sure About This Action , This Action Will Remove And Cancel The Current Whole Connection ?')) return;
    setLoading(true);
    try {
      await axios.post(`${db_url}api/v1/pf-config/unsubscribe`);
      Notify('success', 'Disconnected Successfully', 'success');
      setSubscribeResult(null);
      fetchConfig();
    } catch (err) {
      Notify('error', err.response?.data?.message || 'Faild Cancellation', 'error');
    } finally {
      setLoading(false);
    }
  };

  const hasCredentials = config?.apiKey;
  const isConnected = config?.lastConnectionTest?.success;
  const isSubscribed = config?.isConfigured;

  return (
    <div style={{ position: 'fixed', top: '50px', left: sidebarWidth, right: 0, bottom: 0, transition: 'left 0.25s cubic-bezier(0.4,0,0.2,1)', overflowY: 'auto', display: 'flex', flexDirection: 'column', fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>

      <section style={{ padding: '16px 20px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
          <Link to="/AllLeadsForAdmin" style={{ textDecoration: 'none', color: isDark ? '#9ca3af' : '#6b7280' }}>Home</Link>
          <span style={{ color: isDark ? '#4b5563' : '#d1d5db' }}>/</span>
          <span style={{ color: isDark ? '#e5e7eb' : '#111827', fontWeight: 600 }}>PF Configuration</span>
        </div>
      </section>

      <section style={{ flex: 1, padding: '20px 20px 48px' }}>
        <h1 style={{ fontSize: '22px', fontWeight: 700, color: isDark ? '#f1f5f9' : '#0f172a', margin: '0 0 4px' }}>Property Finder — Setup</h1>
        <p style={{ fontSize: 13, color: isDark ? '#6b7280' : '#64748b', margin: '0 0 24px' }}>
          Connect this CRM to a Property Finder account in 3 steps.
        </p>

        {/* ══ TWO-COLUMN LAYOUT: Guide (left) | Wizard (right) ══ */}
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 24, alignItems: 'start' }}>

          {/* ══════════════════ LEFT: GUIDE (always visible) ══════════════════ */}
          <div style={{ backgroundColor: isDark ? '#1e2132' : '#fff', borderRadius: 16, padding: 22, border: isDark ? '1px solid #2a2f45' : '1px solid #e8eaf0', position: 'sticky', top: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18 }}>
              <span style={{ display: 'flex', color: isDark ? '#93c5fd' : '#1d4ed8' }}><IconInfo /></span>
              <div style={{ fontSize: 14, fontWeight: 700, color: isDark ? '#f1f5f9' : '#0f172a' }}>
                How do I get my API Key & Secret?
              </div>
            </div>

            <GuideStep n={1} title="Open PF Expert" isDark={isDark}>
              Go to <a href="https://app.propertyfinder.ae" target="_blank" rel="noreferrer" style={{ color: isDark ? '#93c5fd' : '#1d4ed8' }}>app.propertyfinder.ae</a> and log in with your agent/advisor account.
            </GuideStep>

            <GuideStep n={2} title="Open Developer Resources" isDark={isDark}>
              In the left sidebar, scroll down to <b>Developer Resources</b>, then click it.
            </GuideStep>

            <GuideStep n={3} title="Go to API Credentials" isDark={isDark}>
              Click <b>API Credentials</b>. You'll see your key stats and an empty list if this is your first time.
              {/* <GuideImage src={Pf_image} alt="PF Expert API Credentials screen" isDark={isDark} onZoom={openLightbox} /> */}
            </GuideStep>

            <GuideStep n={4} title='Click "New API Key"' isDark={isDark}>
              Top-right red button.
                            <GuideImage src={Pf_image} alt="PF Expert API Credentials screen" isDark={isDark} onZoom={openLightbox} />

            </GuideStep>

            <GuideStep n={5} title="Choose the scopes" isDark={isDark}>
              Select: <b>Users:Full_Access </b>,<b>Listings: Full Access</b>, <b>Leads: read</b> ✅, <b>Credits: none</b>, <b>Roles: read</b>,<b>Statistics:read</b>.
              Set a name (e.g. "CRM Integration"), type <b>API Integration</b>, and an expiration date.
              {/* <GuideImage src={Pf_imagescopes} alt="PF Expert scopes selection screen" isDark={isDark} onZoom={openLightbox} /> */}
            </GuideStep>

            <GuideStep n={6} title="Copy your credentials immediately" isDark={isDark}>
              After clicking Create, your <b>API Key</b> and <b>API Secret</b> appear once.
              <span style={{ color: '#ef4444', fontWeight: 700 }}> Copy the Secret now</span> — it will never be shown again after you close this window.
            </GuideStep>

            <GuideStep n={7} title="Paste them on the right" isDark={isDark}>
              Paste the Key and Secret into the fields on the right and click <b>Save Credentials</b>.
            </GuideStep>
          </div>

          {/* ══════════════════ RIGHT: WIZARD (3 steps) ══════════════════ */}
          <div>
            {/* STEP 1 */}
            <div style={{ backgroundColor: isDark ? '#1e2132' : '#fff', borderRadius: 16, padding: 22, border: isDark ? '1px solid #2a2f45' : '1px solid #e8eaf0', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <StepBadge n={1} active={!hasCredentials} done={hasCredentials} isDark={isDark} />
                <div style={{ fontSize: 14, fontWeight: 700, color: isDark ? '#f1f5f9' : '#0f172a' }}>API Credentials</div>
              </div>

              {hasCredentials && (
                <p style={{ fontSize: 12, color: '#16a34a', marginBottom: 12 }}>
                  ✓ Saved — Key: {config.apiKey}
                </p>
              )}
              <div style={{ marginBottom: 12 }}>
                <label style={labelSt(isDark)}>API Key</label>
                <input style={inputSt(isDark)} value={apiKey} onChange={e => setApiKey(e.target.value)} placeholder="from PF Expert → Developer Resources" />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={labelSt(isDark)}>API Secret</label>
                <input type="password" style={inputSt(isDark)} value={apiSecret} onChange={e => setApiSecret(e.target.value)} placeholder="••••••••••••" />
              </div>
              <button onClick={handleSaveCredentials} disabled={loading} style={{ padding: '10px 18px', borderRadius: 9, border: 'none', background: '#1d4ed8', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                Save Credentials
              </button>
            </div>

            {/* STEP 2 */}
            <div style={{ backgroundColor: isDark ? '#1e2132' : '#fff', borderRadius: 16, padding: 22, border: isDark ? '1px solid #2a2f45' : '1px solid #e8eaf0', marginBottom: 16, opacity: hasCredentials ? 1 : 0.5, pointerEvents: hasCredentials ? 'auto' : 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <StepBadge n={2} active={hasCredentials && !isConnected} done={isConnected} isDark={isDark} />
                <div style={{ fontSize: 14, fontWeight: 700, color: isDark ? '#f1f5f9' : '#0f172a' }}>Test Connection</div>
              </div>
              <button onClick={handleTestConnection} disabled={loading} style={{ padding: '10px 18px', borderRadius: 9, border: 'none', background: '#1d4ed8', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                Test Connection
              </button>
              {testResult && (
                <p style={{ fontSize: 12, marginTop: 10, color: testResult.success ? '#16a34a' : '#ef4444' }}>
                  {testResult.success ? '✓' : '✗'} {testResult.message}
                </p>
              )}
            </div>

            {/* STEP 3 */}
            <div style={{ backgroundColor: isDark ? '#1e2132' : '#fff', borderRadius: 16, padding: 22, border: isDark ? '1px solid #2a2f45' : '1px solid #e8eaf0', opacity: isConnected || isSubscribed ? 1 : 0.5, pointerEvents: isConnected || isSubscribed ? 'auto' : 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <StepBadge n={3} active={!isSubscribed} done={isSubscribed} isDark={isDark} />
                <div style={{ fontSize: 14, fontWeight: 700, color: isDark ? '#f1f5f9' : '#0f172a' }}>Activate Webhooks</div>
              </div>
              <p style={{ fontSize: 12, color: isDark ? '#6b7280' : '#94a3b8', marginBottom: 14 }}>
                This will automatically detect the current domain (<code>{window.location.origin}</code>) and register it with Property Finder for lead.created, lead.updated and lead.assigned events.
              </p>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={handleSubscribe} disabled={loading} style={{ padding: '10px 18px', borderRadius: 9, border: 'none', background: '#16a34a', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                  {isSubscribed ? 'Re-activate' : 'Activate Integration'}
                </button>
                {isSubscribed && (
                  <button onClick={handleUnsubscribe} disabled={loading} style={{ padding: '10px 18px', borderRadius: 9, border: `1.5px solid #ef4444`, background: 'transparent', color: '#ef4444', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                    Disconnect
                  </button>
                )}
              </div>

              {(subscribeResult || config?.subscriptions?.length > 0) && (
                <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {(subscribeResult?.subscriptions || config?.subscriptions || []).map((s) => (
                    <div key={s.eventId} style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 8, color: s.status === 'active' ? '#16a34a' : '#ef4444' }}>
                      {s.status === 'active' ? '✓' : '✗'} {s.eventId}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Fullscreen image lightbox */}
      <ImageLightbox src={lightbox.src} alt={lightbox.alt} onClose={closeLightbox} />
    </div>
  );
};
